import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";
import { Check, X, Truck, Receipt, ShieldAlert, Clock, ChevronLeft } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const AdminReturnDetail = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/admin/returns/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    };
    useEffect(load, [id]); // eslint-disable-line

    const action = async (path, payload) => {
        setBusy(path);
        try {
            await api.post(`/admin/returns/${id}/${path}`, payload || {});
            toast.success("Done");
            load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Action failed");
        } finally {
            setBusy(null);
        }
    };

    if (loading || !data) {
        return (
            <AdminAuthGuard><AdminLayout><p className="text-sm text-zinc-500">Loading…</p></AdminLayout></AdminAuthGuard>
        );
    }
    const ret = data.return;
    const order = data.order;
    const customer = data.customer;

    return (
        <AdminAuthGuard>
            <AdminLayout>
                <Link to="/admin/returns" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:underline"><ChevronLeft className="h-3 w-3" /> Queue</Link>
                <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <p className="label-uppercase text-zinc-500">Return</p>
                        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{ret.order_no} • {ret.status.replace("_", " ")}</h1>
                        <p className="mt-1 text-xs text-zinc-500">
                            {customer?.full_name} • {customer?.email} • Requested {new Date(ret.created_at).toLocaleString()}
                        </p>
                    </div>
                    {ret.ai_validation?.flagged && (
                        <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                            <ShieldAlert className="h-3.5 w-3.5" /> AI flagged (conf {ret.ai_validation?.confidence ?? "—"})
                        </span>
                    )}
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-6">
                        {/* Items */}
                        <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                            <p className="label-uppercase text-zinc-500">Items</p>
                            <ul className="mt-3 space-y-3">
                                {(ret.items || []).map((it, i) => (
                                    <li key={i} className="rounded-xl border border-zinc-100 p-4 dark:border-white/10">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-medium text-zinc-900 dark:text-zinc-50">{it.product_name}</p>
                                                <p className="mt-0.5 text-xs text-zinc-500">Qty {it.qty_returned} • {inr(it.unit_price)} • Reason: {it.reason.replace("_", " ")}</p>
                                                {it.notes && <p className="mt-2 text-xs text-zinc-600">{it.notes}</p>}
                                            </div>
                                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{inr(it.qty_returned * it.unit_price)}</p>
                                        </div>
                                        {(it.photos || []).length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {(it.photos || []).map((p) => (
                                                    <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer" className="block h-24 w-24 overflow-hidden rounded-md border border-zinc-200">
                                                        <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="h-full w-full object-cover" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {/* AI validation details */}
                        {ret.ai_validation?.per_photo?.length > 0 && (
                            <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                                <p className="label-uppercase text-zinc-500">AI photo validation</p>
                                <ul className="mt-3 space-y-2">
                                    {ret.ai_validation.per_photo.map((p, i) => (
                                        <li key={i} className="rounded-md border border-zinc-100 p-3 text-xs dark:border-white/10">
                                            {p.error ? (
                                                <p className="text-rose-700">Error: {p.error}</p>
                                            ) : (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className={`rounded-full px-2 py-0.5 font-medium ${p.flagged ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
                                                        {p.supports_reason ? "Supports reason" : "Doesn't support"} ({(p.confidence * 100).toFixed(0)}%)
                                                    </span>
                                                    {(p.fraud_signals || []).map((s) => <span key={s} className="rounded-full bg-rose-100 px-2 py-0.5 font-medium text-rose-700">{s}</span>)}
                                                    <span className="text-zinc-600 dark:text-zinc-300">{p.description}</span>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* History */}
                        <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                            <p className="label-uppercase text-zinc-500">Activity</p>
                            <ol className="mt-3 space-y-2 text-xs">
                                {(ret.history || []).slice().reverse().map((h, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <Clock className="mt-0.5 h-3 w-3 text-zinc-400" />
                                        <span className="text-zinc-500">{new Date(h.at).toLocaleString()}</span>
                                        <span className="text-zinc-700 dark:text-zinc-300">{h.event.replace("_", " ")}</span>
                                        {h.note && <span className="text-zinc-500">— {h.note}</span>}
                                    </li>
                                ))}
                            </ol>
                        </section>
                    </div>

                    {/* Action panel */}
                    <aside className="space-y-4">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                            <p className="label-uppercase text-zinc-500">Order context</p>
                            <p className="mt-2 text-sm font-medium text-zinc-900 dark:text-zinc-50">{order?.order_no}</p>
                            <p className="text-xs text-zinc-500">Total {inr(order?.amounts?.total)} • {order?.payment?.provider}</p>
                            <Link to={`/admin/orders/${order?.id}`} className="mt-2 inline-block text-xs text-brand-700 hover:underline">View order →</Link>
                        </section>

                        <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03]">
                            <p className="label-uppercase text-zinc-500">Actions</p>
                            <div className="mt-3 space-y-2">
                                {ret.status === "requested" && (
                                    <>
                                        <button onClick={() => action("approve")} disabled={busy} className="w-full rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-return-approve">
                                            <Check className="mr-1 inline h-3.5 w-3.5" /> Approve
                                        </button>
                                        <button onClick={() => {
                                            const reason = window.prompt("Reason for rejection:");
                                            if (reason) action("reject", { reason });
                                        }} className="w-full rounded-md border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50" data-testid="admin-return-reject">
                                            <X className="mr-1 inline h-3.5 w-3.5" /> Reject
                                        </button>
                                    </>
                                )}
                                {ret.status === "approved" && (
                                    <button onClick={() => action("mark-picked-up")} disabled={busy} className="w-full rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-return-pickup">
                                        <Truck className="mr-1 inline h-3.5 w-3.5" /> Mark picked up
                                    </button>
                                )}
                                {ret.status === "picked_up" && (
                                    <>
                                        <button onClick={() => {
                                            const notes = window.prompt("Inspection notes (optional):", "Looks good — refund full amount.");
                                            action("inspect", { action: "accept", notes: notes || "", restock_items: true });
                                        }} disabled={busy} className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="admin-return-inspect-accept">
                                            <Check className="mr-1 inline h-3.5 w-3.5" /> Accept &amp; queue refund
                                        </button>
                                        <button onClick={() => {
                                            const notes = window.prompt("Why are you rejecting after inspection?");
                                            if (notes) action("inspect", { action: "reject", notes });
                                        }} className="w-full rounded-md border border-rose-200 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50" data-testid="admin-return-inspect-reject">
                                            <X className="mr-1 inline h-3.5 w-3.5" /> Reject after inspection
                                        </button>
                                    </>
                                )}
                                {ret.status === "inspected" && (
                                    <button onClick={() => action("process-refund")} disabled={busy} className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="admin-return-process-refund">
                                        <Receipt className="mr-1 inline h-3.5 w-3.5" /> Process refund {inr(ret.refund?.amount)}
                                    </button>
                                )}
                                {ret.status === "refunded" && (
                                    <p className="rounded-md bg-emerald-50 p-3 text-xs text-emerald-700">
                                        Refunded {inr(ret.refund?.amount)} via {ret.refund?.method?.replace("_", " ")} on {new Date(ret.refund?.processed_at).toLocaleString()}
                                        {ret.refund?.zoho_refund_id && <><br /><span className="font-mono">{ret.refund.zoho_refund_id}</span></>}
                                    </p>
                                )}
                                {(ret.status === "rejected" || ret.status === "cancelled") && (
                                    <p className="rounded-md bg-rose-50 p-3 text-xs text-rose-700">{ret.status === "cancelled" ? "Cancelled by customer." : `Rejected: ${ret.inspector_notes || "—"}`}</p>
                                )}
                            </div>
                        </section>
                    </aside>
                </div>
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminReturnDetail;
